import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './DeviceForm.css';
import DocumentManager from '../../../shared/components/common/DocumentManager';
import StatusNotice from '../../../shared/components/common/StatusNotice';
import DeviceEditFormHeader from './DeviceEditFormHeader';
import DeviceIssuesSummary from './DeviceIssuesSummary';
import DeviceSectionNav from './DeviceSectionNav';
import { DeviceIDBadge, CriticalityBadge } from '../../../shared/components/common/Badges';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { formatDateForInput } from '../../../utils/dateUtils';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';

function DeviceForm({ device, onSubmit, onCancel, onReturnToDashboard, onSubmissionSuccess, token, user, onNavigateToComplaintsWithFilter = null }) {

  // Role-based permissions:
  // Admin: Full CRUD access to all fields
  // Manager: Create and edit devices (cannot delete)
  // Other roles: Should not access this form (view-only)
  const isAdmin = user && user.role === 'admin';
  const isManager = user && user.role === 'manager';

  const isPendingApproval = device && device.device_status === 'pending_approval';
  const isInDraft = device && device.device_status === 'in_draft';

  // Track when device has been successfully submitted for approval
  // Shows success banner and disables all form fields
  const [isSubmittedForReapproval, setIsSubmittedForReapproval] = useState(false);

  // Device info is read-only when:
  // - Device is pending approval, OR
  // - User is neither admin nor manager with an existing device
  const isDeviceInfoReadOnly = isPendingApproval || (!isAdmin && !isManager && device);

  // Maintenance dates are editable by admin and manager, except when pending approval
  const isMaintenanceDateEditable = (isAdmin || isManager) && !isPendingApproval;

  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [departmentsError, setDepartmentsError] = useState(null);
  const [deviceNames, setDeviceNames] = useState([]);
  const [deviceNamesLoading, setDeviceNamesLoading] = useState(true);
  const [deviceNamesError, setDeviceNamesError] = useState(null);
  const [custodians, setCustodians] = useState([]);
  const [custodiansLoading, setCustodiansLoading] = useState(false);
  const [custodiansError, setCustodiansError] = useState(null);
  const [approval, setApproval] = useState(null);

  // Technician Assignment State
  const [technicians, setTechnicians] = useState([]);
  const [techniciansLoading, setTechniciansLoading] = useState(false);
  const [techniciansError, setTechniciansError] = useState(null);
  const [technicianAssignments, setTechnicianAssignments] = useState({
    PPM: { primary_technician_id: '', secondary_technician_id: '' },
    CALIBRATION: { primary_technician_id: '', secondary_technician_id: '' },
    COMPLIANCE: { primary_technician_id: '', secondary_technician_id: '' }
  });

  // Define min (-30d) and max(today) for Installation Date
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  // Helper function to calculate installation date bounds
  // For new devices: last 30 days
  // For existing devices: ±30 days from current installation date
  const getInstallationDateBounds = () => {
    if (!device || !device.installation_date) {
      return {
        min: thirtyDaysAgo.toISOString().split('T')[0],
        max: today.toISOString().split('T')[0]
      };
    }

    // For existing devices: allow ±30 days correction window
    const currentDate = new Date(device.installation_date);
    const thirtyDaysEarlier = new Date(currentDate);
    thirtyDaysEarlier.setDate(thirtyDaysEarlier.getDate() - 30);

    const thirtyDaysLater = new Date(currentDate);
    thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);

    // Never allow future dates (even with the +30 days buffer)
    const maxDate = thirtyDaysLater < today ? thirtyDaysLater : today;

    return {
      min: thirtyDaysEarlier.toISOString().split('T')[0],
      max: maxDate.toISOString().split('T')[0]
    };
  };

  const [formData, setFormData] = useState({
    device_name_id: '',
    model: '',
    serial_number: '',
    year_of_manufacture: '',
    manufacturer: '',
    supplier: '',
    installation_date: '',
    product_cost: '',
    custodian_employee_id: '',
    department_id: '',
    department_change_reason: '',
    physical_location: '',
    warranty_period: '',
    warranty_expiry: '',
    service_contract_type: '',
    service_contract_period: '',
    service_contract_cost: '',
    contract_start_date: '',
    contract_expiry: '',
    ppm_frequency: '',
    ppm_last_done_date: '',
    ppm_next_due_date: '',
    calibration_required: '',
    calibration_frequency: '',
    calibration_last_done_date: '',
    calibration_next_due_date: '',
    compliance_required: '',
    compliance_type: '',
    compliance_frequency: '',
    compliance_last_done_date: '',
    compliance_next_due_date: '',
    purchase_date: '',
    notes: ''
  });




  // Fetch departments from API
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setDepartmentsLoading(true);
        const response = await fetch(API_ENDPOINTS.DEPARTMENTS.LIST, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setDepartments(data.departments || []);
          setDepartmentsError(null);
        } else {
          setDepartmentsError('Failed to load departments');
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
        setDepartmentsError('Error loading departments');
      } finally {
        setDepartmentsLoading(false);
      }
    };

    if (token) {
      fetchDepartments();
    }
  }, [token]);

  // Fetch device names from API
  useEffect(() => {
    const fetchDeviceNames = async () => {
      try {
        setDeviceNamesLoading(true);
        const response = await fetch(API_ENDPOINTS.DEVICE_NAMES.LIST, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setDeviceNames(data.device_names || []);
          setDeviceNamesError(null);
        } else {
          setDeviceNamesError('Failed to load device names');
        }
      } catch (error) {
        console.error('Error fetching device names:', error);
        setDeviceNamesError('Error loading device names');
      } finally {
        setDeviceNamesLoading(false);
      }
    };

    if (token) {
      fetchDeviceNames();
    }
  }, [token]);

  // Fetch custodians (users) by department
  useEffect(() => {
    const fetchCustodians = async () => {
      if (!formData.department_id) {
        setCustodians([]);
        setCustodiansError(null);
        return;
      }

      try {
        setCustodiansLoading(true);
        const response = await fetch(API_ENDPOINTS.USERS.BY_ROLE('custodian', { department_id: formData.department_id }), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Backend returns array directly, not wrapped in 'users' key
          setCustodians(Array.isArray(data) ? data : (data.users || []));
          setCustodiansError(null);
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.error || errorData.message || `Failed to load custodians (${response.status})`;
          console.error('Failed to load custodians:', response.status, errorData);
          setCustodiansError(errorMessage);
        }
      } catch (error) {
        console.error('Error fetching custodians:', error);
        setCustodiansError('Error loading custodians');
      } finally {
        setCustodiansLoading(false);
      }
    };

    if (token) {
      fetchCustodians();
    }
  }, [formData.department_id, token]);

  // Fetch Technicians for technician assignment (filtered by department)
  // Also fetch for new devices to validate that Technicians exist before creation
  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        setTechniciansLoading(true);
        // Only fetch if department is selected, otherwise clear the list
        if (!formData.department_id) {
          setTechnicians([]);
          setTechniciansError(null);
          return;
        }

        const url = API_ENDPOINTS.USERS.TECHNICIANS({ department_id: formData.department_id });
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setTechnicians(Array.isArray(data) ? data : []);
          setTechniciansError(null);
        } else {
          setTechniciansError('Failed to load Technicians');
        }
      } catch (error) {
        console.error('Error fetching Technicians:', error);
        setTechniciansError('Error loading Technicians');
      } finally {
        setTechniciansLoading(false);
      }
    };

    if (token) {
      // Fetch for both new and existing devices whenever department changes
      fetchTechnicians();
    }
  }, [token, formData.department_id]);

  // Fetch technician assignments for existing device
  useEffect(() => {
    const fetchTechnicianAssignments = async () => {
      if (!device || !device.id) {
        return;
      }

      try {
        const response = await fetch(API_ENDPOINTS.DEVICES.TECHNICIANS(device.id), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();

          // Handle case where no assignment exists (data is null)
          if (!data) {
            setTechnicianAssignments({
              PPM: { primary_technician_id: '', secondary_technician_id: '' },
              CALIBRATION: { primary_technician_id: '', secondary_technician_id: '' },
              COMPLIANCE: { primary_technician_id: '', secondary_technician_id: '' }
            });
            return;
          }

          // Backend now returns single assignment object (not array)
          // Apply same assignment to all three maintenance types
          const assignment = {
            primary_technician_id: data.primary_technician?.id || '',
            secondary_technician_id: data.secondary_technician?.id || ''
          };

          setTechnicianAssignments({
            PPM: assignment,
            CALIBRATION: assignment,
            COMPLIANCE: assignment
          });
        }
      } catch (error) {
        console.error('Error fetching technician assignments:', error);
      }
    };

    if (token && device && device.id) {
      fetchTechnicianAssignments();
    }
  }, [token, device]);

  // Fetch approval status for existing device
  useEffect(() => {
    const fetchApprovalStatus = async () => {
      if (!device || !device.id) {
        return;
      }

      try {
        const response = await fetch(API_ENDPOINTS.APPROVALS.BY_DEVICE(device.id), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.approvals && data.approvals.length > 0) {
            setApproval(data.approvals[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching approval status:', error);
      }
    };

    if (token && device && device.id) {
      fetchApprovalStatus();
    }
  }, [token, device]);

  // Refresh device from backend when editing a rejected device
  // This ensures device_status is always up-to-date
  useEffect(() => {
    const refreshDeviceStatus = async () => {
      if (!device || !device.id || !token) {
        return;
      }

      // Only refresh if device might be in rejected status or in_draft
      // (after admin rejection, device.device_status should be 'in_draft')
      if (device.device_status === 'pending_approval' || device.device_status === 'in_draft') {
        try {
          const response = await fetch(API_ENDPOINTS.DEVICES.GET(device.id), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const freshDevice = await response.json();
            // If server status differs from form's device prop, the parent component
            // has stale data. This is OK - we'll use server data for submission
            if (freshDevice.device_status !== device.device_status) {
              console.warn('⚠️ Device status mismatch detected:', {
                formHas: device.device_status,
                serverHas: freshDevice.device_status,
                message: 'Parent component may have stale device data. Server data will be authoritative.'
              });
            }
          }
        } catch (error) {
          console.error('Error refreshing device status from server:', error);
        }
      }
    };

    // Delay refresh slightly to avoid rapid repeated calls
    const timer = setTimeout(refreshDeviceStatus, 300);
    return () => clearTimeout(timer);
  }, [device?.id, device?.device_status, token]);

  // Check if this is an edit-resubmit scenario (device was rejected by admin and manager is re-editing)
  // vs initial draft creation (first time manager saved the device)
  const isEditResubmitAfterRejection = device && device.device_status === 'in_draft' && approval?.approval_status === 'rejected';


  useEffect(() => {
    if (device) {
      // Helper function to format numeric values to currency format
      const formatNumericValue = (value) => {
        if (!value) return '';
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return value;

        // Convert to string and handle decimal
        const strValue = numValue.toString();
        const hasDecimal = strValue.includes('.');
        let integerPart = strValue.split('.')[0];
        let decimalPart = strValue.includes('.') ? strValue.split('.')[1] : '';

        // Format integer part with thousands separators
        const integerValue = parseInt(integerPart || '0', 10);
        const formattedInteger = integerValue.toLocaleString('en-IN', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });

        // Combine with decimal part
        if (hasDecimal && decimalPart) {
          return `${formattedInteger}.${decimalPart}`;
        }
        return formattedInteger;
      };

      setFormData({
        device_name_id: device.device_name_id || '',
        model: device.model || '',
        serial_number: device.serial_number || '',
        year_of_manufacture: device.year_of_manufacture || '',
        manufacturer: device.manufacturer || '',
        supplier: device.supplier || '',
        installation_date: formatDateForInput(device.installation_date),
        product_cost: formatNumericValue(device.product_cost),
        custodian_employee_id: device.custodian_employee_id || '',
        department_id: device.department_id || '',
        physical_location: device.physical_location || '',
        warranty_period: device.warranty_period || '',
        warranty_expiry: formatDateForInput(device.warranty_expiry),
        service_contract_type: device.service_contract_type || '',
        service_contract_period: device.service_contract_period || '',
        service_contract_cost: formatNumericValue(device.service_contract_cost),
        contract_start_date: formatDateForInput(device.contract_start_date),
        contract_expiry: formatDateForInput(device.contract_expiry),
        ppm_frequency: device.ppm_frequency || '',
        ppm_last_done_date: formatDateForInput(device.ppm_last_done_date),
        ppm_next_due_date: formatDateForInput(device.ppm_next_due_date),
        calibration_required: device.calibration_required || '',
        calibration_frequency: device.calibration_frequency || '',
        calibration_last_done_date: formatDateForInput(device.calibration_last_done_date),
        calibration_next_due_date: formatDateForInput(device.calibration_next_due_date),
        compliance_required: device.compliance_required || '',
        compliance_type: device.compliance_type || '',
        compliance_frequency: device.compliance_frequency || '',
        compliance_last_done_date: formatDateForInput(device.compliance_last_done_date),
        compliance_next_due_date: formatDateForInput(device.compliance_next_due_date),
        purchase_date: formatDateForInput(device.purchase_date),
        notes: device.notes || ''
      });
    }
  }, [device]);

  // Auto-calculate warranty expiry date when installation date or warranty period changes
  // IMPORTANT: Only auto-calculate when service_contract_type is "Warranty"
  // This prevents unwanted auto-calculation for LAMC/CAMC contracts
  // Also only calculate if user is creating a new device, not editing existing ones
  useEffect(() => {
    if (!device && formData.service_contract_type === 'Warranty' && formData.installation_date && formData.warranty_period) {
      const installationDate = new Date(formData.installation_date);
      const years = parseInt(formData.warranty_period, 10);

      // Add years to installation date
      const expiryDate = new Date(installationDate);
      expiryDate.setFullYear(expiryDate.getFullYear() + years);
      // Subtract 1 day → makes expiry = 364 days
      expiryDate.setDate(expiryDate.getDate() - 1);

      // Format date as YYYY-MM-DD for the input field
      const formattedDate = expiryDate.toISOString().split('T')[0];

      setFormData(prev => ({
        ...prev,
        warranty_expiry: formattedDate
      }));
    }
  }, [formData.installation_date, formData.warranty_period, formData.service_contract_type, device]);

  // Auto-calculate contract expiry date when contract start date or service contract period changes
  // IMPORTANT: Only auto-calculate when service_contract_type is "LAMC" or "CAMC"
  // Also only calculate if user is creating a new device, not editing existing ones
  useEffect(() => {
    if (!device && (formData.service_contract_type === 'LAMC' || formData.service_contract_type === 'CAMC') && formData.contract_start_date && formData.service_contract_period) {
      const contractStartDate = new Date(formData.contract_start_date);
      const years = parseInt(formData.service_contract_period, 10);

      // Add years to contract start date
      const expiryDate = new Date(contractStartDate);
      expiryDate.setFullYear(expiryDate.getFullYear() + years);
      // Subtract 1 day → makes expiry = 364 days (contract ends on the last day of the period)
      expiryDate.setDate(expiryDate.getDate() - 1);

      // Format date as YYYY-MM-DD for the input field
      const formattedDate = expiryDate.toISOString().split('T')[0];

      setFormData(prev => ({
        ...prev,
        contract_expiry: formattedDate
      }));
    }
  }, [formData.contract_start_date, formData.service_contract_period, formData.service_contract_type, device]);

  // Clear non-applicable contract fields when service_contract_type changes
  // CRITICAL FIX: When user changes contract type, orphaned fields from previous type should be cleared
  // This ensures database doesn't store invalid data like warranty_period when type='LAMC'
  useEffect(() => {
    setFormData(prev => {
      const updated = { ...prev };
      const contractType = updated.service_contract_type;

      if (contractType === 'Warranty') {
        // Clear LAMC/CAMC fields - they don't apply to Warranty contracts
        updated.service_contract_period = '';
        updated.service_contract_cost = '';
        updated.contract_start_date = '';
        updated.contract_expiry = '';
      } else if (contractType === 'LAMC' || contractType === 'CAMC') {
        // Clear Warranty fields - they don't apply to LAMC/CAMC contracts
        updated.warranty_period = '';
        updated.warranty_expiry = '';
      } else if (contractType === 'In-house') {
        // Clear ALL date and cost fields - In-house has no external contract fields
        updated.warranty_period = '';
        updated.warranty_expiry = '';
        updated.service_contract_period = '';
        updated.service_contract_cost = '';
        updated.contract_start_date = '';
        updated.contract_expiry = '';
      }
      // If contract type is empty, don't clear anything yet (user is still selecting)

      return updated;
    });
  }, [formData.service_contract_type]);
  // Helper function to safely add months to a date, handling month-end dates
  const addMonths = (date, months) => {
    const d = new Date(date);
    let month = d.getMonth() + months;
    let year = d.getFullYear();

    // Handle year overflow
    year += Math.floor(month / 12);
    month = month % 12;

    // Handle day overflow (e.g., Jan 31 + 1 month = Feb 28/29, not Mar 3)
    const day = d.getDate();
    d.setFullYear(year, month, 1); // Set to first day of target month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    d.setDate(Math.min(day, daysInMonth)); // Set to original day, or last day if month is shorter

    return d;
  };

  // Auto-calculate PPM Next Due (only when creating new device or when user explicitly changes the values)
  useEffect(() => {
  const { ppm_frequency, ppm_last_done_date } = formData;

  // Only auto-calculate if:
  // 1. We're creating a new device (no device.id) AND
  // 2. Both frequency and last_done_date are provided
  // For existing devices, user can manually edit the next_due_date
  if (!device && ppm_frequency && ppm_last_done_date) {
    const lastDone = new Date(ppm_last_done_date);
    let nextDue = new Date(lastDone);

    // Apply frequency rules
    if (ppm_frequency === 'Quarterly') {
      nextDue = addMonths(lastDone, 3);
    } else if (ppm_frequency === 'Half Yearly') {
      nextDue = addMonths(lastDone, 6);
    } else if (ppm_frequency === 'Yearly') {
      nextDue.setFullYear(nextDue.getFullYear() + 1);
    }

    // Format YYYY-MM-DD
    const formatted = nextDue.toISOString().split("T")[0];

    setFormData(prev => ({
      ...prev,
      ppm_next_due_date: formatted
    }));
  }
   }, [formData.ppm_frequency, formData.ppm_last_done_date, device]);
    // Auto-calculate Calibration Next Due (only when creating new device)
  useEffect(() => {
  const { calibration_required, calibration_frequency, calibration_last_done_date } = formData;

  // Only calculate if:
  // 1. We're creating a new device (no device.id) AND
  // 2. Calibration is required AND both frequency and last_done_date are filled
  // For existing devices, user can manually edit the next_due_date
  if (!device && calibration_required === "Yes" && calibration_frequency && calibration_last_done_date) {
    const lastDone = new Date(calibration_last_done_date);
    let nextDue = new Date(lastDone);

    if (calibration_frequency === "Quarterly") {
      nextDue = addMonths(lastDone, 3);
    } else if (calibration_frequency === "Half Yearly") {
      nextDue = addMonths(lastDone, 6);
    } else if (calibration_frequency === "Yearly") {
      nextDue.setFullYear(nextDue.getFullYear() + 1);
    }

    const formatted = nextDue.toISOString().split("T")[0];

    setFormData(prev => ({
      ...prev,
      calibration_next_due_date: formatted,
    }));
  }
}, [
  formData.calibration_required,
  formData.calibration_frequency,
  formData.calibration_last_done_date,
  device
]);
  useEffect(() => {
  const { compliance_required, compliance_frequency, compliance_last_done_date } = formData;

  // Only calculate if:
  // 1. We're creating a new device (no device.id) AND
  // 2. Compliance is required AND both frequency and last_done_date are filled
  // For existing devices, user can manually edit the next_due_date
  if (!device && compliance_required === "Yes" && compliance_frequency && compliance_last_done_date) {
    const lastDone = new Date(compliance_last_done_date);
    let nextDue = new Date(lastDone);

    if (compliance_frequency === "Quarterly") {
      nextDue = addMonths(lastDone, 3);
    } else if (compliance_frequency === "Half Yearly") {
      nextDue = addMonths(lastDone, 6);
    } else if (compliance_frequency === "Yearly") {
      nextDue.setFullYear(nextDue.getFullYear() + 1);
    }

    const formatted = nextDue.toISOString().split("T")[0];

    setFormData(prev => ({
      ...prev,
      compliance_next_due_date: formatted,
    }));
  }
}, [
  formData.compliance_required,
  formData.compliance_frequency,
  formData.compliance_last_done_date,
  device
]);
  // Helper function to format currency input in real-time
  const formatCurrencyInput = (value) => {
    // Allow empty value
    if (value === '') {
      return '';
    }

    // Remove all non-digit characters except decimal point
    let cleanValue = value.replace(/[^\d.]/g, '');

    // Handle multiple decimal points - keep only the first one and limit to 2 decimals
    if (cleanValue.includes('.')) {
      const parts = cleanValue.split('.');
      cleanValue = parts[0] + '.' + (parts[1] ? parts[1].substring(0, 2) : '');
    }

    // If only decimal point, return it to allow user to continue typing
    if (cleanValue === '.') {
      return '.';
    }

    // If empty after cleaning, return empty
    if (cleanValue === '') {
      return '';
    }

    // If ends with decimal (like "100."), return as-is to allow user to type decimals
    if (cleanValue.endsWith('.')) {
      return cleanValue;
    }

    // Parse the numeric value
    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) {
      return cleanValue;
    }

    // If value is 0 or very small (like 0.01), return as-is without formatting
    if (numValue === 0) {
      return cleanValue;
    }

    // Split into integer and decimal parts to handle formatting correctly
    const hasDecimal = cleanValue.includes('.');
    let integerPart = cleanValue.split('.')[0];
    let decimalPart = cleanValue.includes('.') ? cleanValue.split('.')[1] : '';

    // Format only the integer part with thousands separators
    const integerValue = parseInt(integerPart || '0', 10);
    const formattedInteger = integerValue.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    // Combine integer and decimal parts
    if (hasDecimal) {
      return decimalPart ? `${formattedInteger}.${decimalPart}` : `${formattedInteger}.`;
    }

    return formattedInteger;
  };

  // Helper function to get raw numeric value from formatted currency
  const getCurrencyNumericValue = (formattedValue) => {
    if (!formattedValue) return '';
    // Remove all non-digit and non-decimal characters
    return formattedValue.replace(/[^\d.]/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special formatting for product_cost - format in real-time with thousands separator
    if (name === 'product_cost') {
      // Allow empty value (user can clear the field)
      if (value === '') {
        setFormData(prev => ({
          ...prev,
          [name]: ''
        }));
        return;
      }

      // Format and update - will handle decimal points and numbers
      const formatted = formatCurrencyInput(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
      return;
    }

    // Special formatting for service_contract_cost - format in real-time with thousands separator
    if (name === 'service_contract_cost') {
      // Allow empty value (user can clear the field)
      if (value === '') {
        setFormData(prev => ({
          ...prev,
          [name]: ''
        }));
        return;
      }

      // Format and update - will handle decimal points and numbers
      const formatted = formatCurrencyInput(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
      return;
    }

    // Special validation for year_of_manufacture - only allow numeric digits
    if (name === 'year_of_manufacture') {
      // Allow empty value (user can clear the field)
      if (value === '') {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        return;
      }

      // Only allow numeric digits - don't validate range while typing
      // User should be able to type "2", "20", "202", "2020", etc. progressively
      if (!/^\d+$/.test(value)) {
        // Non-numeric input - don't update
        return;
      }

      // Valid numeric input - allow user to continue typing
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      return;  // Exit early, don't call the generic setFormData below
    }

    // Special validation for warranty_period - only allow numeric positive integers
    if (name === 'warranty_period') {
      // Allow empty value (user can clear the field)
      if (value === '') {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        return;
      }

      // Check if value contains only digits
      if (!/^[0-9]+$/.test(value)) {
        // Invalid input - don't update state
        return;
      }

      // Check if it's a positive number (no leading zeros for multi-digit numbers)
      const numValue = parseInt(value, 10);
      if (numValue === 0) {
        // Don't allow zero
        return;
      }
    }

    // Special validation for service_contract_period - only allow numeric positive integers
    if (name === 'service_contract_period') {
      // Allow empty value (user can clear the field)
      if (value === '') {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        return;
      }

      // Check if value contains only digits
      if (!/^[0-9]+$/.test(value)) {
        // Invalid input - don't update state
        return;
      }

      // Check if it's a positive number (no leading zeros for multi-digit numbers)
      const numValue = parseInt(value, 10);
      if (numValue === 0) {
        // Don't allow zero
        return;
      }
    }

    // Special handling for department_id change
    if (name === 'department_id' && value === '') {
      // When department is cleared, also clear technician assignments with a warning
      setTechnicianAssignments({
        PPM: { primary_technician_id: '', secondary_technician_id: '' },
        CALIBRATION: { primary_technician_id: '', secondary_technician_id: '' },
        COMPLIANCE: { primary_technician_id: '', secondary_technician_id: '' }
      });
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required basic fields
    if (!formData.department_id) {
      toast.error('Department is required. Please select a department before saving.');
      return;
    }

    if (!formData.installation_date) {
      toast.error('Installation Date is required. Please provide the installation date.');
      return;
    }

    if (!formData.product_cost) {
      toast.error('Product Cost is required. Please provide the product cost.');
      return;
    }

    // Validate that product_cost is a valid number
    const productCostRaw = getCurrencyNumericValue(formData.product_cost);
    if (!productCostRaw || isNaN(parseFloat(productCostRaw))) {
      toast.error('Product Cost must be a valid number.');
      return;
    }

    if (!formData.year_of_manufacture) {
      toast.error('Year of Manufacture is required. Please provide the year of manufacture.');
      return;
    }

    // Validate year of manufacture is a valid integer between 1970 and current year
    const yearValue = parseInt(formData.year_of_manufacture, 10);
    const currentYear = new Date().getFullYear();

    if (isNaN(yearValue) || yearValue.toString() !== formData.year_of_manufacture) {
      toast.error('Year of Manufacture must be a valid integer.');
      return;
    }

    if (yearValue > currentYear) {
      toast.error(`Year of Manufacture cannot be greater than the current year (${currentYear}).`);
      return;
    }

    if (yearValue < 1970) {
      toast.error('Year of Manufacture must be 1970 or later.');
      return;
    }

    if (!formData.service_contract_type) {
      toast.error('Service Contract Type is required. Please select a service contract type.');
      return;
    }

    // Validate custodian is assigned
    if (!formData.custodian_employee_id || formData.custodian_employee_id.trim() === '') {
      toast.error('Custodian Name is required. Please select a custodian for the device.');
      return;
    }

    // Notes are mandatory for managers creating new devices
    if (!device && isManager && (!formData.notes || formData.notes.trim() === '')) {
      toast.error('Additional Notes are required for device approval. Please add details and justifications that will be reviewed during the approval process.');
      return;
    }

    // Validate service contract type-specific fields
    if (formData.service_contract_type === 'Warranty') {
      if (!formData.warranty_period) {
        toast.error('Warranty Period is required for Warranty contracts.');
        return;
      }
      if (!formData.warranty_expiry) {
        toast.error('Warranty Expiry Date is required for Warranty contracts.');
        return;
      }
    }

    if (formData.service_contract_type === 'LAMC' || formData.service_contract_type === 'CAMC') {
      if (!formData.service_contract_period) {
        toast.error('Service Contract Period is required for LAMC/CAMC contracts.');
        return;
      }
      if (!formData.service_contract_cost) {
        toast.error('Service Contract Cost is required for LAMC/CAMC contracts.');
        return;
      }
      if (!formData.contract_start_date) {
        toast.error('Contract Start Date is required for LAMC/CAMC contracts.');
        return;
      }
      if (!formData.contract_expiry) {
        toast.error('Contract Expiry Date is required for LAMC/CAMC contracts.');
        return;
      }
    }

    // Validate that at least one Technician exists for the selected department
    // This applies to both new and existing devices
    if (technicians.length === 0) {
      toast.error('Cannot save device: No Technicians exist for the selected department. Please create at least one Technician first.');
      return;
    }

    // Validate that at least one technician is assigned
    let hasAnyTechnicianAssigned = false;
    for (const maintenanceType of Object.keys(technicianAssignments)) {
      const { primary_technician_id, secondary_technician_id } = technicianAssignments[maintenanceType];

      // Check if at least one technician is assigned for this maintenance type
      if (primary_technician_id || secondary_technician_id) {
        hasAnyTechnicianAssigned = true;
      }

      // Validate that primary and secondary technicians are not the same
      if (primary_technician_id && secondary_technician_id && primary_technician_id === secondary_technician_id) {
        toast.error('Cannot assign the same Technician as both primary and secondary technician for maintenance activities');
        return;
      }
    }

    // If no technician is assigned at all, show error
    if (!hasAnyTechnicianAssigned) {
      toast.error('Please assign at least one Technician for device maintenance activities.');
      return;
    }

    const departmentChanged =
      device &&
      String(formData.department_id || '') !== String(device.department_id || '');
    if (departmentChanged && !(formData.department_change_reason || '').trim()) {
      toast.error('Correction reason is required when changing department.');
      return;
    }

    try {
      // Prepare form data with formatted values converted to plain numbers
      const submitFormData = {
        ...formData,
        product_cost: getCurrencyNumericValue(formData.product_cost),
        service_contract_cost: getCurrencyNumericValue(formData.service_contract_cost),
        // For managers creating new devices: set status to 'in_draft' (Save Draft)
        // For admins: status will be created as 'active'
        ...(isManager && !device && { device_status: 'in_draft' })
      };
      if (!departmentChanged) {
        delete submitFormData.department_change_reason;
      } else {
        submitFormData.department_change_reason = formData.department_change_reason.trim();
      }

      // Handle technician assignments data
      const techniciansToSave = [];
      Object.keys(technicianAssignments).forEach(maintenanceType => {
        const { primary_technician_id, secondary_technician_id } = technicianAssignments[maintenanceType];

        // Only save if at least one technician is assigned
        if (primary_technician_id || secondary_technician_id) {
          techniciansToSave.push({
            maintenance_type: maintenanceType,
            primary_technician_id: primary_technician_id ? Number(primary_technician_id) : null,
            secondary_technician_id: secondary_technician_id ? Number(secondary_technician_id) : null
          });
        }
      });

      // Add technician assignments to submitFormData for new devices
      // Backend will handle creating the assignments after device is created
      if (!device) {
        submitFormData.technicians = techniciansToSave;
      }

      // Save technician assignments before device update (edit flow closes the form on onSubmit success)
      if (device && device.id && techniciansToSave.length > 0) {
        const assignment = techniciansToSave[0];
        const techResponse = await fetch(API_ENDPOINTS.DEVICES.TECHNICIANS(device.id), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            primary_technician_id: assignment.primary_technician_id,
            secondary_technician_id: assignment.secondary_technician_id
          })
        });

        if (!techResponse.ok) {
          const errBody = await techResponse.json().catch(() => ({}));
          toast.error(errBody.error || 'Failed to save technician assignment');
          return;
        }
      }

      // Submit device data (await so technician save completes before form closes)
      if (device) {
        await onSubmit(device.id, submitFormData);
      } else {
        await onSubmit(submitFormData);
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      toast.error('Failed to save device');
    }
  };


  const handleSubmitForApproval = async () => {
    // For new devices without an ID yet, we need to save it first
    // This creates the device so documents can be attached
    if (!device || !device.id) {
      toast.error('Device must be saved before submitting for approval. Please save the device first.');
      console.error('⚠️ handleSubmitForApproval: device is null or has no ID. User should save device first.');
      return;
    }

    // CRITICAL: Validate that notes field still has data
    // Manager may have removed notes after saving draft - this must be prevented
    if (!formData.notes || formData.notes.trim() === '') {
      toast.error('Additional Notes are required for device approval. Please add details and justifications that will be reviewed during the approval process.');
      return;
    }

    try {
      // STEP 1: Save any pending changes first
      // Prepare form data with formatted values converted to plain numbers
      const submitFormData = {
        ...formData,
        product_cost: getCurrencyNumericValue(formData.product_cost),
        service_contract_cost: getCurrencyNumericValue(formData.service_contract_cost)
      };

      // Save the device first
      const saveResponse = await fetch(API_ENDPOINTS.DEVICES.UPDATE(device.id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitFormData)
      });

      if (!saveResponse.ok) {
        console.error('⚠️ Failed to save device changes:', saveResponse.status);
        toast.error('Failed to save your changes. Please try again.');
        return;
      }

      // STEP 2: Refresh device status from server
      const freshResponse = await fetch(API_ENDPOINTS.DEVICES.GET(device.id), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!freshResponse.ok) {
        console.error('⚠️ Failed to fetch fresh device status:', freshResponse.status);
        toast.error('Failed to verify device status. Please refresh and try again.');
        return;
      }

      const freshDevice = await freshResponse.json();

      // IMPORTANT: Use server status (freshDevice) for all subsequent checks, not the stale device prop
      if (freshDevice.device_status !== 'in_draft') {
        console.error('❌ Device status check failed:', {
          expected: 'in_draft',
          actual: freshDevice.device_status,
          deviceId: freshDevice.id,
          deviceIdentifier: freshDevice.device_identifier,
          message: 'Device must be in in_draft status to submit for approval'
        });
        toast.error(`Only devices in draft status can be submitted for approval. Current status: ${freshDevice.device_status}`);
        return;
      }

      // STEP 3: Submit for approval
      const response = await fetch(API_ENDPOINTS.DEVICES.SUBMIT_FOR_APPROVAL(freshDevice.id), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        let errorMessage = 'Failed to submit device for approval';
        try {
          const errorData = await response.json();
          console.error('Backend error:', errorData);
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        toast.error(errorMessage);
        return;
      }

      const result = await response.json();

      // IMPORTANT: Switch form to view mode (read-only) after successful submission
      // User will see confirmation message and "Go back to Dashboard" button
      setIsSubmittedForReapproval(true);

      // Show success toast message
      toast.success(`Device submitted for approval! Device ID: ${result.device_identifier}`);

      // Scroll to top to show confirmation banner
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Notify parent component of successful submission
      // This allows the parent (Devices.js) to close the form and refresh the device list
      if (onSubmissionSuccess) {
        onSubmissionSuccess(result);
      }
    } catch (error) {
      console.error('Error submitting device for approval:', error);
      toast.error('An error occurred while submitting the device for approval: ' + error.message);
    }
  };

  const selectedDeviceName = deviceNames.find(
    (dn) => String(dn.id) === String(formData.device_name_id)
  );

  const primaryTechnicianId = technicianAssignments.PPM?.primary_technician_id || '';
  const secondaryTechnicianId = technicianAssignments.PPM?.secondary_technician_id || '';
  const selectedPrimaryTechnician = technicians.find(
    (tech) => String(tech.id) === String(primaryTechnicianId)
  );
  const selectedSecondaryTechnician = technicians.find(
    (tech) => String(tech.id) === String(secondaryTechnicianId)
  );
  const techniciansConflict =
    Boolean(primaryTechnicianId) &&
    Boolean(secondaryTechnicianId) &&
    String(primaryTechnicianId) === String(secondaryTechnicianId);

  const updateTechnicianRole = (role, technicianId) => {
    setTechnicianAssignments((prev) => ({
      PPM: { ...prev.PPM, [role]: technicianId },
      CALIBRATION: { ...prev.CALIBRATION, [role]: technicianId },
      COMPLIANCE: { ...prev.COMPLIANCE, [role]: technicianId },
    }));
  };

  // Edit: keep the device's originally assigned name selectable for the whole
  // session if it is inactive (missing from the active-only list). Pin on
  // device.device_name_id — not the current form value — so switching away
  // and back remains possible without canceling the edit.
  const deviceNameOptions = (() => {
    const options = [...deviceNames];
    const assignedId = device?.device_name_id;
    if (
      assignedId &&
      !options.some((dn) => String(dn.id) === String(assignedId))
    ) {
      options.unshift({
        id: assignedId,
        name: device.name || device.device_name || 'Current device name',
        category: device.device_category || device.category || '',
        criticality: device.criticality || '',
        is_inactive_current: true,
      });
    }
    return options;
  })();
  const selectedDeviceNameOption = deviceNameOptions.find(
    (dn) => String(dn.id) === String(formData.device_name_id)
  );
  const isInactiveCurrentDeviceName = Boolean(selectedDeviceNameOption?.is_inactive_current);
  const selectedCriticality = device?.criticality || selectedDeviceName?.criticality;
  const selectedDepartment = departments.find(
    (dept) => String(dept.id) === String(formData.department_id)
  );
  const headerDepartmentName =
    selectedDepartment?.name || device?.department_name || null;
  const headerDepartmentIcon =
    selectedDepartment?.icon || device?.department_icon || '🏢';
  const headerLocationName = device?.location_name || user?.location_name || null;
  const headerTitle = device
    ? (device.name || device.device_name || 'Device')
    : (selectedDeviceName?.name || 'Add New Device');
  const showHeaderBadges = Boolean(
    selectedCriticality || headerDepartmentName || headerLocationName
  );

  const formSectionLinks = [
    { id: 'device-section-basic', label: 'Basic' },
    { id: 'device-section-warranty', label: 'Warranty' },
    { id: 'device-section-ppm', label: 'PPM' },
    { id: 'device-section-calibration', label: 'Calibration' },
    { id: 'device-section-compliance', label: 'Compliance' },
    ...(formData.department_id
      ? [{ id: 'device-section-technicians', label: 'Technicians' }]
      : []),
    { id: 'device-section-notes', label: 'Notes' },
    ...(device?.id
      ? [{ id: 'device-section-documents', label: 'Documents' }]
      : []),
  ];

  const scrollToFormSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="device-view-container device-form-page">
      <div className="device-view-header">
        <div className="header-content">
          <div className="header-title-row">
            <h2>{headerTitle}</h2>
            {!device && selectedDeviceName && (
              <span className="header-mode-cue">New device</span>
            )}
            {device && device.device_identifier && (
              <span className="device-id-badge-header">
                <DeviceIDBadge deviceId={device.device_identifier} size="small" />
              </span>
            )}
          </div>
          {showHeaderBadges && (
            <div className="header-badges" aria-live="polite">
              {selectedCriticality && (
                <CriticalityBadge criticality={selectedCriticality} size="small" />
              )}
              {headerDepartmentName && (
                <span className="header-department">
                  <span className="department-icon-large" aria-hidden="true">
                    {headerDepartmentIcon}
                  </span>
                  <span className="department-badge-large">{headerDepartmentName}</span>
                </span>
              )}
              {headerLocationName && (
                <span className="location-badge-large">
                  <span className="location-badge-pin" aria-hidden="true">📍</span>
                  <span className="location-badge-text">{headerLocationName}</span>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="header-actions">
          <div className="action-palette">
            {/* Hide all buttons when submitted for approval */}
            {!isSubmittedForReapproval && (
              <>
                {/* For managers creating new device: Show "Save Draft" button (will show Submit for Approval after device is created) */}
                {!device && isManager ? (
                  <button type="submit" form="device-form" className="action-btn action-primary" title="Save device as draft - you'll be able to upload documents and submit for approval after">
                    <span className="action-icon">💾</span>
                    <span className="action-text">Save Draft</span>
                  </button>
                ) : (
                  <button type="submit" form="device-form" className="action-btn action-primary" disabled={isPendingApproval || isSubmittedForReapproval} title={isSubmittedForReapproval ? 'Device has been submitted for approval' : isPendingApproval ? 'Device is pending approval and cannot be edited' :''}>
                <span className="action-icon">{device ? '✓' : '➕'}</span>
                <span className="action-text">{device ? (isInDraft ? 'Update & Save Draft' : 'Update Device') : 'Add Device'}</span>
              </button>
            )}
                {isInDraft && (
                  <button type="button" className="action-btn action-primary" onClick={handleSubmitForApproval} style={{ backgroundColor: '#2196F3', borderColor: '#1976D2' }} title="Submit this device for admin approval">
                    <span className="action-icon">📤</span>
                    <span className="action-text">Submit for Approval</span>
                  </button>
                )}
                <button type="button" className="action-btn action-secondary" onClick={onCancel}>
                  <span className="action-icon">←</span>
                  <span className="action-text">Cancel</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>


      {/* Attention banner — same rich chips as device view */}
      {device && (
        <DeviceIssuesSummary
          device={device}
          user={user}
          onNavigateToComplaintsWithFilter={onNavigateToComplaintsWithFilter}
          hidden={isPendingApproval}
        />
      )}

      <DeviceSectionNav
        links={formSectionLinks}
        onNavigate={scrollToFormSection}
      />

      <div className="device-view-content">
        {/* Device Edit Form Header - Show approval workflow info */}
        {device && (
          <DeviceEditFormHeader
            deviceStatus={device.device_status}
            approvalStatus={approval?.approval_status}
            rejectionReason={approval?.decision_reason}
            resubmissionCount={approval?.resubmission_count}
            requestedAt={approval?.requested_at}
          />
        )}

        {/* Draft Mode Banner */}
        {device && isInDraft && !isSubmittedForReapproval && (
          <StatusNotice
            variant="info"
            icon={isEditResubmitAfterRejection ? '✎' : undefined}
            title={isEditResubmitAfterRejection ? 'Device Edit Mode' : 'Draft Saved'}
          >
            {isEditResubmitAfterRejection
              ? 'You are now editing this device to address the admin\'s feedback. Make the necessary changes and click "Update & Save Draft" to save your edits. When you\'re ready, click "Submit for Approval" to resubmit the device to the admin for review.'
              : 'Your device has been saved as a draft. You can continue editing, upload supporting documents, and submit for admin approval whenever you\'re ready. Click "Submit for Approval" to send this device to the admin for review.'}
          </StatusNotice>
        )}

        {/* Submitted for Approval Success Banner */}
        {isSubmittedForReapproval && (
          <StatusNotice
            variant="success"
            title="Device Submitted Successfully!"
            actions={
              onReturnToDashboard ? (
                <button type="button" className="action-btn action-primary" onClick={onReturnToDashboard}>
                  <span className="action-icon" aria-hidden="true">🏠</span>
                  <span className="action-text">Go back to Dashboard</span>
                </button>
              ) : null
            }
          >
            Your device has been submitted for admin approval. The admin will review your changes and
            notify you once a decision is made. You can now return to the dashboard to continue with
            other tasks.
          </StatusNotice>
        )}

        <form onSubmit={handleSubmit} id="device-form" className="device-form">
          {/* Basic Device Information */}
          <div className="info-card" id="device-section-basic">
            <h3>Basic Device Information</h3>
            <div className="info-grid">
              <div className="form-group">
                <label htmlFor="device_name_id">
                  Device Name *
                  {deviceNamesLoading && <span className="loading-indicator">⟳</span>}
                </label>
                {deviceNamesError && (
                  <div className="field-error">{deviceNamesError}</div>
                )}
                <select
                  id="device_name_id"
                  name="device_name_id"
                  value={formData.device_name_id}
                  onChange={handleChange}
                  required
                  disabled={deviceNamesLoading || (isDeviceInfoReadOnly && device) || isPendingApproval}
                  className={`${deviceNamesError ? 'field-error-state' : ''} ${(isDeviceInfoReadOnly && device) ? 'read-only-field' : ''}`}
                  title={isPendingApproval ? 'Device is pending approval and cannot be edited' : ''}
                >
                  <option value="">
                    {deviceNamesLoading ? 'Loading device names...' : 'Select Device Name'}
                  </option>
                  {deviceNameOptions.map((dn) => (
                    <option key={dn.id} value={dn.id}>
                      {dn.name} ({dn.category}
                      {dn.criticality
                        ? ` — ${dn.criticality.charAt(0).toUpperCase()}${dn.criticality.slice(1)}`
                        : ''}
                      )
                    </option>
                  ))}
                </select>
                {!deviceNamesLoading && deviceNames.length > 0 && !isInactiveCurrentDeviceName && (
                  <small className="field-hint">
                    {deviceNames.length} names available
                  </small>
                )}
                {isInactiveCurrentDeviceName && (
                  <small className="field-hint">
                    Current name is inactive — kept on this device, not available for new ones.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="model">Model *</label>
                <input
                  type="text"
                  id="model"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  required
                  disabled={isPendingApproval}
                  readOnly={isDeviceInfoReadOnly && device && !isPendingApproval}
                  className={isDeviceInfoReadOnly && device ? 'read-only-field' : ''}
                  placeholder="e.g., XR-2000"
                  title={isPendingApproval ? 'Device is pending approval and cannot be edited' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="serial_number">Serial Number *</label>
                <input
                  type="text"
                  id="serial_number"
                  name="serial_number"
                  value={formData.serial_number}
                  onChange={handleChange}
                  required
                  disabled={isPendingApproval}
                  readOnly={isDeviceInfoReadOnly && device && !isPendingApproval}
                  className={isDeviceInfoReadOnly && device ? 'read-only-field' : ''}
                  placeholder="e.g., SN123456789"
                  title={isPendingApproval ? 'Device is pending approval and cannot be edited' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="year_of_manufacture">Year of Manufacture *</label>
                <input
                  type="text"
                  id="year_of_manufacture"
                  name="year_of_manufacture"
                  value={formData.year_of_manufacture}
                  onChange={handleChange}
                  required
                  disabled={isPendingApproval}
                  readOnly={isDeviceInfoReadOnly && device && !isPendingApproval}
                  className={isDeviceInfoReadOnly && device ? 'read-only-field' : ''}
                  placeholder="e.g., 2020"
                  title={isPendingApproval ? 'Device is pending approval and cannot be edited' : `Please enter a valid year between 1970 and ${new Date().getFullYear()}`}
                />
              </div>

              <div className="form-group">
                <label htmlFor="manufacturer">Manufacturer *</label>
                <input
                  type="text"
                  id="manufacturer"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleChange}
                  required
                  disabled={isPendingApproval}
                  readOnly={isDeviceInfoReadOnly && device && !isPendingApproval}
                  className={isDeviceInfoReadOnly && device ? 'read-only-field' : ''}
                  placeholder="e.g., Siemens"
                  title={isPendingApproval ? 'Device is pending approval and cannot be edited' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="supplier">Supplier</label>
                <input
                  type="text"
                  id="supplier"
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleChange}
                  disabled={isPendingApproval}
                  placeholder="e.g., MedTech Supplies Ltd"
                />
              </div>

              {/* Product Cost - Show for admin and manager */}
              {(isAdmin || isManager) && (
                <div className="form-group">
                  <label htmlFor="product_cost">Product Cost (₹) *</label>
                  <input
                    type="text"
                    id="product_cost"
                    name="product_cost"
                    value={formData.product_cost}
                    onChange={handleChange}
                    required
                    disabled={isPendingApproval}
                    placeholder="e.g., 50,00,000.00"
                    title={isPendingApproval ? 'Device is pending approval and cannot be edited' :"Enter product cost in rupees. Formatting with thousands separators will be applied automatically."}
                  />
                </div>
              )}

              {/* Row with Department, Custodian Name, and Physical Location */}
              <div className="form-group">
                <label htmlFor="department_id">Department *</label>
                {departmentsError && (
                  <div className="field-error">{departmentsError}</div>
                )}

                <select
                  id="department_id"
                  name="department_id"
                  value={formData.department_id}
                  onChange={handleChange}
                  required
                  disabled={departmentsLoading || (isDeviceInfoReadOnly && device) || isPendingApproval}
                  className={isDeviceInfoReadOnly && device ? 'read-only-field' : ''}
                  title={isPendingApproval ? 'Device is pending approval and cannot be edited' : ''}
                >
                  <option value="">
                    {departmentsLoading ? 'Loading departments...' : 'Select Department'}
                  </option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.icon} {dept.name}
                    </option>
                  ))}
                </select>
                {device && (
                  <small className="field-hint">
                    Use this to correct a wrong department from create/import. Custodian handoffs use Transfer Department.
                  </small>
                )}
              </div>

              {device &&
                String(formData.department_id || '') !== String(device.department_id || '') && (
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="department_change_reason">Correction reason *</label>
                  <textarea
                    id="department_change_reason"
                    name="department_change_reason"
                    value={formData.department_change_reason}
                    onChange={handleChange}
                    rows={3}
                    required
                    disabled={isPendingApproval}
                    placeholder="Explain why the department is being corrected"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="custodian_employee_id">
                  Custodian Name *
                  {custodiansLoading && <span className="loading-indicator">⟳</span>}
                </label>
                <select
                  id="custodian_employee_id"
                  name="custodian_employee_id"
                  value={formData.custodian_employee_id}
                  onChange={handleChange}
                  disabled={!formData.department_id || custodiansLoading || isPendingApproval}
                  title={isPendingApproval ? 'Device is pending approval and cannot be edited' :''}
                >
                  <option value="">
                    {!formData.department_id ? 'Select Department First' : custodiansLoading ? 'Loading custodians...' : custodians.length === 0 ? 'No custodians available' : 'Select Custodian'}
                  </option>
                  {custodians.map((custodian) => (
                    <option key={custodian.id} value={custodian.employee_id}>
                      {custodian.full_name} ({custodian.employee_id})
                    </option>
                  ))}
                </select>
                {custodiansError && (
                  <div className="field-error">{custodiansError}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="physical_location">Physical Location *</label>
                <input
                  type="text"
                  id="physical_location"
                  name="physical_location"
                  value={formData.physical_location}
                  onChange={handleChange}
                  required
                  disabled={isPendingApproval}
                  placeholder="e.g., Building A - Room 203"
                />
              </div>
            </div>
          </div>

        {/* Warranty & Service Contract Information */}
        <div className="info-card" id="device-section-warranty">
          <h3>Warranty & Service Contract</h3>
          <div className="info-grid">
            <div className="form-group">
              <label htmlFor="installation_date">Installation Date *</label>
              <input
                type="date"
                id="installation_date"
                name="installation_date"
                value={formData.installation_date}
                onChange={handleChange}
                min = {getInstallationDateBounds().min}
                max = {getInstallationDateBounds().max}
                required
                disabled={isPendingApproval}
                title={device ? `Edit mode: Correction window ±30 days from current date (${device.installation_date}). For larger corrections, use Data Import.` : 'Installation date must be within the last 30 days. For earlier dates, use Data Import.'}
              />
              {!device ? (
                <small className="field-hint">
                  Installation date must be within the last 30 days when creating new devices. For devices installed earlier, use Data Import.
                </small>
              ) : (
                <small className="field-hint">
                  For corrections: you can modify the installation date within ±30 days of the current date ({device.installation_date}). For larger corrections, re-import the device row via Data Import with its device identifier.
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="service_contract_type">Service Contract Type *</label>
              <select
                id="service_contract_type"
                name="service_contract_type"
                value={formData.service_contract_type}
                onChange={handleChange}
                required
                disabled={isPendingApproval}
              >
                <option value="">Select Type</option>
                <option value="Warranty">Warranty</option>
                <option value="LAMC">LAMC</option>
                <option value="CAMC">CAMC</option>
                <option value="In-house">In-house</option>
              </select>
            </div>
          </div>

          {/* Warranty fields - show only when "Warranty" is selected */}
          {formData.service_contract_type === 'Warranty' && (
            <div className="info-grid">
              <div className="form-group">
                <label htmlFor="warranty_period">Warranty Period (Years) *</label>
                <input
                  type="text"
                  id="warranty_period"
                  name="warranty_period"
                  value={formData.warranty_period}
                  onChange={handleChange}
                  required
                  disabled={isPendingApproval}
                  placeholder="e.g., 1, 2, 3"
                  pattern="[0-9]+"
                  title="Please enter only numeric values (1, 2, 3, etc.)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="warranty_expiry">Warranty Expiry Date *</label>
                <input
                  type="date"
                  id="warranty_expiry"
                  name="warranty_expiry"
                  value={formData.warranty_expiry}
                  onChange={handleChange}
                  required
                  disabled={isPendingApproval}
                />
              </div>
            </div>
          )}

          {/* Service Contract fields - show only when "LAMC" or "CAMC" is selected */}
          {(formData.service_contract_type === 'LAMC' || formData.service_contract_type === 'CAMC') && (
            <div className="info-grid">
              <div className="form-group">
                <label htmlFor="service_contract_period">Service Contract Period (Years) *</label>
                <input
                  type="text"
                  id="service_contract_period"
                  name="service_contract_period"
                  value={formData.service_contract_period}
                  onChange={handleChange}
                  required
                  disabled={isPendingApproval}
                  placeholder="e.g., 1, 2, 3"
                  pattern="[0-9]+"
                  title="Please enter only numeric values (1, 2, 3, etc.)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="service_contract_cost">Service Contract Cost (₹) *</label>
                <input
                  type="text"
                  id="service_contract_cost"
                  name="service_contract_cost"
                  value={formData.service_contract_cost}
                  onChange={handleChange}
                  required
                  disabled={isPendingApproval}
                  placeholder="e.g., 5,00,000.00"
                  title={isPendingApproval ? 'Device is pending approval and cannot be edited' :"Enter service contract cost in rupees. Formatting with thousands separators will be applied automatically."}
                />
                {/* Yearly cost breakup */}
                {/* NOTE: This yearly cost breakdown (totalCost / serviceContractPeriod) should be used in:
                    1. Yearly cost analysis reports
                    2. Budget spend tracking and forecasting
                    3. Financial dashboards and analytics
                    4. Contract renewal planning and cost projections
                    The formula: Yearly Cost = Total Service Contract Cost / Service Contract Period (Years)
                */}
                {formData.service_contract_period && formData.service_contract_cost && (() => {
                  const years = parseInt(formData.service_contract_period, 10);
                  const totalCostRaw = getCurrencyNumericValue(formData.service_contract_cost);
                  const totalCost = parseFloat(totalCostRaw) || 0;
                  const yearlyAmount = (totalCost / years).toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  });

                  const yearlyBreakup = Array.from({ length: years }, (_, i) =>
                    `Year ${i + 1}: ₹${yearlyAmount}`
                  ).join(' | ');

                  return (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 10px',
                      backgroundColor: '#f0f4ff',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#333',
                      border: '1px solid #e0e7ff'
                    }}>
                      <strong>Yearly Breakdown:</strong> {yearlyBreakup}
                    </div>
                  );
                })()}
              </div>
              
              <div className="form-group">
                <label htmlFor="contract_start_date">Contract Start Date *</label>
                <input
                  type="date"
                  id="contract_start_date"
                  name="contract_start_date"
                  value={formData.contract_start_date}
                  onChange={handleChange}
                  required
                  disabled={isPendingApproval}
                />
              </div>

              <div className="form-group">
                <label htmlFor="contract_expiry">Contract Expiry Date *</label>
                <input
                  type="date"
                  id="contract_expiry"
                  name="contract_expiry"
                  value={formData.contract_expiry}
                  onChange={handleChange}
                  required
                  disabled={isPendingApproval}
                />
              </div>
            </div>
          )}
        </div>

        {/* PPM (Planned Preventive Maintenance) Information */}
        <div className="info-card" id="device-section-ppm">
          <h3>PPM (Planned Preventive Maintenance)</h3>
          <div className="info-grid">
            <div className="form-group">
              <label htmlFor="ppm_frequency">PPM Frequency</label>
              <select
                id="ppm_frequency"
                name="ppm_frequency"
                value={formData.ppm_frequency}
                onChange={handleChange}
                disabled={isPendingApproval}
              >
                <option value="">Select Frequency</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Half Yearly">Half Yearly</option>
                <option value="Yearly">Yearly</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="ppm_last_done_date">PPM Last Done Date</label>
              <input
                type="date"
                id="ppm_last_done_date"
                name="ppm_last_done_date"
                value={formData.ppm_last_done_date}
                onChange={handleChange}
                max = {today.toISOString().split('T')[0]}
                disabled={isPendingApproval}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ppm_next_due_date">PPM Next Due Date</label>
              <input
                type="date"
                id="ppm_next_due_date"
                name="ppm_next_due_date"
                value={formData.ppm_next_due_date}
                onChange={handleChange}
                disabled={!isMaintenanceDateEditable || isPendingApproval}
                className={!isMaintenanceDateEditable ? 'read-only-field' : ''}
                title={isPendingApproval ? 'Device is pending approval and cannot be edited' : ''}
              />
            </div>
          </div>
        </div>
         
        

        {/* Calibration Information */}
        <div className="info-card" id="device-section-calibration">
          <h3>Calibration / Validation</h3>
          <div className="info-grid">
            <div className="form-group">
              <label htmlFor="calibration_required">Calibration Required</label>
              <select
                id="calibration_required"
                name="calibration_required"
                value={formData.calibration_required}
                onChange={handleChange}
                disabled={isPendingApproval}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            {/* Show further fields ONLY if calibration required = Yes */}
            {formData.calibration_required === "Yes" && (
              <>
                <div className="form-group">
                  <label htmlFor="calibration_frequency">Calibration Frequency</label>
                  <select
                    id="calibration_frequency"
                    name="calibration_frequency"
                    value={formData.calibration_frequency}
                    onChange={handleChange}
                    disabled={isPendingApproval}
                  >
                    <option value="">Select Frequency</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Half Yearly">Half Yearly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="calibration_last_done_date">Calibration Last Done Date</label>
                  <input
                    type="date"
                    id="calibration_last_done_date"
                    name="calibration_last_done_date"
                    value={formData.calibration_last_done_date}
                    onChange={handleChange}
                    max = {today.toISOString().split('T')[0]}
                    disabled={isPendingApproval}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="calibration_next_due_date">Calibration Next Due Date</label>
                  <input
                    type="date"
                    id="calibration_next_due_date"
                    name="calibration_next_due_date"
                    value={formData.calibration_next_due_date}
                    onChange={handleChange}
                    disabled={!isMaintenanceDateEditable || isPendingApproval}
                    className={!isMaintenanceDateEditable ? 'read-only-field' : ''}
                    title={isPendingApproval ? 'Device is pending approval and cannot be edited' : ''}
                  />
                </div>
              </>
            )}
          </div>
        </div> 
        
        {/* Regulatory Compliance */}
        <div className="info-card" id="device-section-compliance">
          <h3>Regulatory Compliance</h3>
          <div className="info-grid">
            <div className="form-group">
              <label htmlFor="compliance_required">Compliance Required</label>
              <select
                id="compliance_required"
                name="compliance_required"
                value={formData.compliance_required}
                onChange={handleChange}
                disabled={isPendingApproval}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            {/* Show Compliance Type ONLY if Compliance Required = Yes */}
            {formData.compliance_required === "Yes" && (
              <div className="form-group">
                <label htmlFor="compliance_type">Compliance Type</label>
                <select
                  id="compliance_type"
                  name="compliance_type"
                  value={formData.compliance_type}
                  onChange={handleChange}
                  disabled={isPendingApproval}
                >
                  <option value="">Select Compliance Type</option>
                  <option value="AERB">AERB</option>
                  <option value="PNDT">PNDT</option>
                  <option value="BARC">BARC</option>
                  <option value="Weight & Measure">Weight & Measure</option>
                </select>
              </div>
            )}

            {/* Show further fields ONLY if Compliance required = Yes */}
            {formData.compliance_required === "Yes" && (
              <>
                <div className="form-group">
                  <label htmlFor="compliance_frequency">Compliance Frequency</label>
                  <select
                    id="compliance_frequency"
                    name="compliance_frequency"
                    value={formData.compliance_frequency}
                    onChange={handleChange}
                    disabled={isPendingApproval}
                  >
                    <option value="">Select Frequency</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Half Yearly">Half Yearly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="compliance_last_done_date">Compliance Last Done Date</label>
                  <input
                    type="date"
                    id="compliance_last_done_date"
                    name="compliance_last_done_date"
                    value={formData.compliance_last_done_date}
                    onChange={handleChange}
                    max = {today.toISOString().split('T')[0]}
                    disabled={isPendingApproval}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="compliance_next_due_date">Compliance Next Due Date</label>
                  <input
                    type="date"
                    id="compliance_next_due_date"
                    name="compliance_next_due_date"
                    value={formData.compliance_next_due_date}
                    onChange={handleChange}
                    disabled={!isMaintenanceDateEditable || isPendingApproval}
                    className={!isMaintenanceDateEditable ? 'read-only-field' : ''}
                    title={isPendingApproval ? 'Device is pending approval and cannot be edited' : ''}
                  />
                </div>
              </>
            )}
          </div>
        </div> 

        {/* Technician Assignment - Show for both new and existing devices */}
        {formData.department_id && (
          <div className="info-card" id="device-section-technicians">
            <h3>🔧 Technician Assignment</h3>
            <p className="tech-assign-intro">
              Technicians assigned for all maintenance activities (PPM, Calibration, Regulatory Compliance) and complaints for this device
            </p>

            {techniciansError && (
              <div className="tech-assign-error">{techniciansError}</div>
            )}

            {technicians.length === 0 && !techniciansLoading && (
              <div className="tech-assign-empty-state">
                <p className="tech-assign-empty-title">No Technicians Available</p>
                <p className="tech-assign-empty-text">
                  No Technician users exist for this department. Please create technicians through the User Management component.
                </p>
              </div>
            )}

            {technicians.length > 0 && (
              <div className={`tech-assign-panel${isPendingApproval ? ' is-disabled' : ''}`}>
                <div className="tech-assign-grid">
                  <div className="tech-assign-slot">
                    <label htmlFor="primary_technician" className="tech-assign-label">
                      Primary Technician *{techniciansLoading && <span className="loading-indicator">⟳</span>}
                    </label>
                    <select
                      id="primary_technician"
                      value={primaryTechnicianId}
                      onChange={(e) => updateTechnicianRole('primary_technician_id', e.target.value)}
                      disabled={!formData.department_id || techniciansLoading || isPendingApproval}
                      className={techniciansConflict ? 'field-error-border' : ''}
                    >
                      <option value="">
                        {techniciansLoading ? 'Loading...' : 'Select Primary Technician'}
                      </option>
                      {technicians.map((engineer) => (
                        <option key={engineer.id} value={engineer.id} title={`${engineer.full_name} (${engineer.email})`}>
                          {engineer.full_name}
                        </option>
                      ))}
                    </select>
                    {selectedPrimaryTechnician ? (
                      <div className="tech-assign-card">
                        <div className="tech-assign-name">{selectedPrimaryTechnician.full_name}</div>
                        <div className="tech-assign-detail">
                          <span className="tech-assign-icon" aria-hidden="true">✉️</span>
                          <span className="tech-assign-detail-text">{selectedPrimaryTechnician.email || 'Not available'}</span>
                        </div>
                        <div className="tech-assign-detail">
                          <span className="tech-assign-icon" aria-hidden="true">📞</span>
                          <span className="tech-assign-detail-text">
                            {selectedPrimaryTechnician.contact_number
                              ? formatPhoneNumber(selectedPrimaryTechnician.contact_number)
                              : 'Not available'}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="tech-assign-card tech-assign-card--empty">
                        <span>ℹ️</span>
                        Not assigned
                      </div>
                    )}
                  </div>

                  <div className="tech-assign-slot">
                    <label htmlFor="secondary_technician" className="tech-assign-label">
                      Secondary Technician (Optional)
                    </label>
                    <select
                      id="secondary_technician"
                      value={secondaryTechnicianId}
                      onChange={(e) => updateTechnicianRole('secondary_technician_id', e.target.value)}
                      disabled={!formData.department_id || techniciansLoading || isPendingApproval}
                      className={techniciansConflict ? 'field-error-border' : ''}
                    >
                      <option value="">None Selected</option>
                      {technicians.map((engineer) => (
                        <option key={engineer.id} value={engineer.id} title={`${engineer.full_name} (${engineer.email})`}>
                          {engineer.full_name}
                        </option>
                      ))}
                    </select>
                    {selectedSecondaryTechnician ? (
                      <div className="tech-assign-card">
                        <div className="tech-assign-name">{selectedSecondaryTechnician.full_name}</div>
                        <div className="tech-assign-detail">
                          <span className="tech-assign-icon" aria-hidden="true">✉️</span>
                          <span className="tech-assign-detail-text">{selectedSecondaryTechnician.email || 'Not available'}</span>
                        </div>
                        <div className="tech-assign-detail">
                          <span className="tech-assign-icon" aria-hidden="true">📞</span>
                          <span className="tech-assign-detail-text">
                            {selectedSecondaryTechnician.contact_number
                              ? formatPhoneNumber(selectedSecondaryTechnician.contact_number)
                              : 'Not available'}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="tech-assign-card tech-assign-card--empty">
                        <span>ℹ️</span>
                        Not assigned
                      </div>
                    )}
                  </div>
                </div>
                {techniciansConflict && (
                  <p className="tech-assign-conflict-hint">
                    Primary and secondary technician must be different people.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Additional Information */}
        <div className="info-card" id="device-section-notes">
          <h3>Additional Information</h3>
          <div className="info-grid">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="notes">
                Notes
                {!device && isManager && <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>}
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                disabled={isPendingApproval}
                placeholder={!device && isManager ? "Add details and justifications for this device. This information will be used as reference during the admin approval process." : "Additional information about the device..."}
                className={(!device && isManager && !formData.notes) ? '' : ''}
              />
              {!device && isManager && (
                <small className="field-hint field-hint--accent">
                  ℹ️ Add details and justifications for this device to help admins make informed approval decisions.
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <div className="action-palette">
            {/* Hide all buttons when submitted for approval */}
            {!isSubmittedForReapproval && (
              <>
                {/* For managers creating new device: Show "Save Draft" button (will show Submit for Approval after device is created) */}
                {!device && isManager ? (
                  <button type="submit" className="action-btn action-primary" title="Save device as draft - you'll be able to upload documents and submit for approval after">
                    <span className="action-icon">💾</span>
                    <span className="action-text">Save Draft</span>
                  </button>
                ) : (
                  <button type="submit" className="action-btn action-primary" disabled={isPendingApproval} title={isPendingApproval ? 'Device is pending approval and cannot be edited' :''}>
                    <span className="action-icon">{device ? '✓' : '➕'}</span>
                    <span className="action-text">{device ? (isInDraft ? 'Update & Save Draft' : 'Update Device') : 'Add Device'}</span>
                  </button>
                )}
                {isInDraft && (
                  <button type="button" className="action-btn action-primary" onClick={handleSubmitForApproval} style={{ backgroundColor: '#2196F3', borderColor: '#1976D2' }} title="Submit this device for admin approval">
                    <span className="action-icon">📤</span>
                    <span className="action-text">Submit for Approval</span>
                  </button>
                )}
                <button type="button" className="action-btn action-secondary" onClick={onCancel}>
                  <span className="action-icon">✕</span>
                  <span className="action-text">Cancel</span>
                </button>
              </>
            )}
          </div>
        </div>
      </form>
  
      {/* Document Manager - CRITICAL for approval process */}
      {/* Show for:
           1. New devices being created (in_draft status)
           2. Existing devices being edited/resubmitted (in_draft status)
           3. Always show documents section after creation (do NOT hide after submission)
           4. DocumentManager component handles upload restrictions based on device_status
      */}
      {token && device && device.id && (
        <div className="document-manager-container" id="device-section-documents">
          <h3 className="document-section-title">📎 Documents & Attachments</h3>
          <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '15px' }}>
            Upload relevant documents that will support the approval process. These documents will be reviewed by the admin along with the device details. <strong>Documents are required before submitting for approval.</strong>
          </p>
          <DocumentManager
            deviceId={device.id}
            token={token}
            user={user}
            deviceStatus={device.device_status}
            isSubmittedForApproval={isSubmittedForReapproval}
          />
        </div>
      )}

      </div>

    </div>
  );
}

export default DeviceForm;
